import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Printer, ImageUp, Tv, Maximize } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import { fmtRange, todayISO } from '@/components/agenda/agendaUtils';
import { SheetLink, SHEET_LINK_CSS } from '@/components/agenda/SheetLink';
import { PROGRESS, ProgressPill } from '@/components/agenda/progress';

const Y = '#FACC15';
const BG = '#0E1116';
const CARD = '#181C23';

const ATELIER_LINKS = [['/atelier/gardiennage', 'Gardiennage'], ['/atelier/pneus-occasion', 'Pneus occasion'], ['/atelier/clients', 'Clients'], ['/atelier/pointage', 'Timbreuse'], ['/atelier/commandes', 'Commande stock']];
const AtelierNav = ({ navigate }) => (
  <div className="flex items-center gap-5 mr-auto" data-testid="planning-atelier-nav">
    {ATELIER_LINKS.map(([to, label]) => (
      <button key={to} onClick={() => navigate(to)} className="text-sm font-medium text-slate-700 hover:text-slate-950 hover:bg-slate-100 rounded-full px-2.5 py-0.5" data-testid={`planning-nav-${to.split('/').pop()}`}>{label}</button>
    ))}
  </div>
);

const ATELIER_CSS = `@media print { .sheet-fit { zoom: 1 !important; } #planning-sheet { height: 210mm !important; } }
.planning-row-click:hover { outline: 0.6mm solid #FACC15; }
.atelier-bar button, .atelier-bar label { color: #fff !important; font-size: clamp(17px, 0.9vw, 28px) !important; font-weight: 700 !important; }
.atelier-bar button:hover { color: #FACC15 !important; background: transparent !important; }
.atelier-bar button svg { width: 20px; height: 20px; }
.atelier-bar input[type=date] { background: #181C23; color: #fff; color-scheme: dark; border-color: #2A303A; font-size: clamp(17px, 0.9vw, 28px); font-weight: 700; padding: 4px 10px; }
.atelier-bar [data-testid=planning-print-btn] { background: #FACC15 !important; color: #0E1116 !important; }`;

// Fit the A4 landscape sheet (297x210mm ≈ 1123x794px) to the screen on the workshop display.
const useFitScale = (enabled) => {
  const calc = () => { const zoom = window.innerWidth / 1123; return { zoom, height: (window.innerHeight - 60) / zoom }; };
  const [scale, setScale] = useState(enabled ? calc() : null);
  useEffect(() => {
    if (!enabled) return undefined;
    const on = () => setScale(calc());
    window.addEventListener('resize', on);
    document.addEventListener('fullscreenchange', on);
    return () => { window.removeEventListener('resize', on); document.removeEventListener('fullscreenchange', on); };
  }, [enabled]);
  return scale;
};

const PRINT_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700;800;900&display=swap');
@page { size: A4 landscape; margin: 0; }
@media print {
  body * { visibility: hidden; }
  #planning-sheet, #planning-sheet * { visibility: visible; }
  #planning-sheet { position: fixed; left: 0; top: 0; box-shadow: none !important; }
  .no-print { display: none !important; }
}
#planning-sheet, #planning-sheet * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
`;

const Label = ({ children, style }) => (
  <div style={{ color: Y, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', ...style }}>{children}</div>
);

const InterventionRow = ({ item, dense, atelier }) => {
  const navigate = useNavigate();
  const open = atelier && item.work_order_id ? () => navigate(`/atelier/work-orders/${item.work_order_id}`) : undefined;
  return (
  <div onClick={open} data-testid={`planning-row-${item.id}`} className={open ? 'planning-row-click' : undefined} style={{ cursor: open ? 'pointer' : undefined, display: 'grid', gridTemplateColumns: '23% 40% 37%', alignItems: 'center', background: item.internal ? '#0F2A1A' : CARD, borderLeft: `1.2mm solid ${item.kind === 'task' ? Y : item.internal ? '#16A34A' : (PROGRESS[item.progress] || PROGRESS.prevu).color}`, borderRadius: '1.5mm', padding: dense ? '1.6mm 4mm' : '3mm 4mm', minHeight: dense ? '9mm' : '15mm', flex: '0 1 auto', overflow: 'hidden' }}>
    <div style={{ color: Y, fontWeight: 800, fontSize: dense ? '3.4mm' : '4.6mm' }}>{item.kind === 'appointment' && <div style={{ marginBottom: '1mm' }}><ProgressPill item={item} /></div>}{item.all_day ? 'Journée' : (item.ranges?.length > 1 ? item.ranges.map(r => fmtRange(r[0], r[1])).join(' / ') : fmtRange(item.time, item.end_time))}</div>
    <div style={{ minWidth: 0 }}>
      <div style={{ color: item.internal ? '#4ADE80' : '#fff', fontWeight: 800, fontSize: dense ? '3.6mm' : '4.8mm', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} data-testid={`planning-title-${item.id}`}><SheetLink to={!atelier && item.client_id && `/admin/clients/${item.client_id}`} title="Ouvrir la fiche client" testid={`planning-client-link-${item.id}`}>{item.title}</SheetLink></div>
      <div style={{ color: '#9AA3AF', fontSize: dense ? '2.8mm' : '3.4mm', marginTop: '0.6mm' }}>{item.subtitle}</div>
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ color: '#fff', fontWeight: 700, fontSize: dense ? '3.4mm' : '4.4mm', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.25 }}>{atelier ? item.work : <SheetLink to={item.work_order_id && `/admin/work-orders/${item.work_order_id}`} title="Ouvrir la fiche atelier" testid={`planning-work-link-${item.id}`}>{item.work}</SheetLink>}</div>
      {item.notes && !dense && <div style={{ color: '#9AA3AF', fontSize: '3mm', marginTop: '0.6mm', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.notes}</div>}
    </div>
  </div>
  );
};

const SidePanel = ({ team, tasks, dayName }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '5mm' }}>
    <Label style={{ fontSize: '4mm' }}>Équipe sur site</Label>
    <div style={{ background: CARD, borderRadius: '2mm', padding: '3mm 5mm' }} data-testid="planning-team">
      {team.length === 0 && <div style={{ color: '#9AA3AF', fontSize: '3.4mm' }}>Aucun employé actif</div>}
      {team.slice(0, 6).map((m, i) => (
        <div key={i} style={{ padding: '2.2mm 0', borderTop: i ? '0.3mm solid #2A303A' : 'none' }}>
          <Label style={{ fontSize: '2.6mm' }}>{m.role || 'Équipe'}</Label>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: '4.6mm', marginTop: '0.6mm' }}>{m.name}</div>
        </div>
      ))}
    </div>
    {tasks.length > 0 && <>
      <Label style={{ fontSize: '4mm', marginTop: '2mm' }}>Atelier • {dayName}</Label>
      <div style={{ background: CARD, borderRadius: '2mm', padding: '4mm 5mm' }}>
        {tasks.slice(0, 3).map((t, i) => (
          <div key={t.id} style={{ paddingTop: i ? '3mm' : 0 }}>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: '5mm' }}>{fmtRange(t.time, t.end_time)}</div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: '4.2mm', marginTop: '1mm' }}>{t.work}</div>
            <div style={{ color: '#9AA3AF', fontSize: '3.3mm', marginTop: '0.8mm' }}>{t.subtitle}</div>
          </div>
        ))}
      </div>
    </>}
  </div>
);

const PlanningSheet = ({ data, logo, atelier, height }) => {
  const d = new Date(`${data.date}T12:00:00`);
  const dayName = d.toLocaleDateString('fr-CH', { weekday: 'long' });
  const dateStr = d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const company = data.company_name || 'Garage';
  const tasks = data.items.filter(i => i.kind === 'task');
  const dense = data.items.length > 7;
  return (
    <div id="planning-sheet" data-testid="planning-sheet" style={{ width: '297mm', height: height ? `${height}px` : '210mm', background: BG, fontFamily: "'Archivo', 'Helvetica Neue', Arial, sans-serif", display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.35)' }}>
      <div style={{ height: '2.2mm', background: Y }} />
      <div style={{ padding: '7mm 11mm 0', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7mm', paddingBottom: '5mm', borderBottom: '0.3mm solid #2A303A' }}>
          <div style={{ width: '32mm', height: '26mm', background: '#000', borderRadius: '1.5mm', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {logo && <img src={logo} alt="logo" style={{ maxWidth: '92%', maxHeight: '92%', objectFit: 'contain' }} data-testid="planning-logo" />}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: '6.5mm', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{company}</div>
            <div style={{ color: '#fff', fontWeight: 900, fontSize: '12.5mm', letterSpacing: '0.03em', lineHeight: 1.1 }}>PLANNING ATELIER</div>
          </div>
          <div style={{ textAlign: 'left', paddingRight: '8mm' }}>
            <Label style={{ fontSize: '6mm' }}>{dayName}</Label>
            <div style={{ color: '#fff', fontWeight: 900, fontSize: '10.5mm' }} data-testid="planning-date">{dateStr}</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 86mm', gap: '10mm', paddingTop: '6mm', flex: 1, minHeight: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2.2mm', minHeight: 0 }}>
            <Label style={{ fontSize: '4mm', marginBottom: '1.5mm' }}>Interventions du jour</Label>
            <div style={{ display: 'grid', gridTemplateColumns: '23% 40% 37%', background: Y, borderRadius: '1.5mm', padding: '2.6mm 4mm', color: '#111', fontWeight: 800, fontSize: '3mm', letterSpacing: '0.06em' }}>
              <div>HORAIRE</div><div>CLIENT / VÉHICULE</div><div>TRAVAUX PRÉVUS</div>
            </div>
            {data.items.length === 0 && <div style={{ color: '#9AA3AF', fontSize: '4mm', padding: '6mm 4mm' }}>Aucune intervention prévue</div>}
            {data.items.slice(0, 12).map(it => <InterventionRow key={it.id} item={it} dense={dense} atelier={atelier} />)}
          </div>
          <SidePanel team={data.team} tasks={tasks} dayName={dayName} />
        </div>
      </div>
      <div style={{ margin: '0 11mm', padding: '4mm 0 5mm', borderTop: '0.3mm solid #2A303A', display: 'flex', justifyContent: 'space-between', fontSize: '3.2mm', fontWeight: 800, letterSpacing: '0.06em' }}>
        <span style={{ color: '#8B93A0', textTransform: 'uppercase' }}>{company}</span>
        <span style={{ color: Y, paddingRight: '8mm' }}>ORGANISATION DE LA JOURNÉE</span>
      </div>
    </div>
  );
};

export default function AgendaPrintPage({ atelier = false }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const date = params.get('date') || todayISO();
  const [data, setData] = useState(null);
  const [logo, setLogo] = useState('');
  const [logoVersion, setLogoVersion] = useState(0);

  useEffect(() => {
    axios.get(`${API}/agenda/day-planning`, { params: { date } }).then(r => setData(r.data)).catch(() => setData({ date, items: [], team: [], company_name: '' }));
  }, [date]);

  useEffect(() => {
    let url = '';
    axios.get(`${API}/agenda/planning-logo`, { responseType: 'blob' })
      .then(r => { url = URL.createObjectURL(r.data); setLogo(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [logoVersion]);

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    try { await axios.post(`${API}/agenda/planning-logo`, fd); toast.success('Logo du planning mis à jour'); setLogoVersion(v => v + 1); }
    catch { toast.error("Erreur lors de l'envoi du logo"); }
  };

  const scale = useFitScale(atelier);
  useEffect(() => {
    if (!atelier) return undefined;
    const t = setInterval(() => axios.get(`${API}/agenda/day-planning`, { params: { date } }).then(r => setData(r.data)).catch(() => {}), 60000);
    return () => clearInterval(t);
  }, [atelier, date]);
  const fullscreen = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()).catch(() => {});

  return (
    <div className={`min-h-screen flex flex-col items-center ${atelier ? 'bg-[#0E1116] h-screen overflow-hidden' : 'bg-slate-200 py-6 gap-4'}`} data-testid="agenda-print-page">
      <style>{PRINT_CSS + SHEET_LINK_CSS + ATELIER_CSS}</style>
      <div className={atelier ? "no-print atelier-bar w-full h-[60px] shrink-0 flex items-center gap-6 bg-[#0E1116] border-b-2 border-[#FACC15] px-6" : "no-print flex items-center gap-3 bg-white rounded-full shadow px-4 py-1.5"} data-testid="planning-toolbar">
        {atelier ? <AtelierNav navigate={navigate} /> : <button onClick={() => navigate('/admin/agenda')} className="flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-950" data-testid="planning-back-btn"><ArrowLeft className="w-4 h-4" />Agenda</button>}
        <input type="date" value={date} onChange={e => setParams({ date: e.target.value })} className="border rounded-md px-2 py-1 text-sm" data-testid="planning-date-input" />
        {atelier ? (
          <>
            <button onClick={fullscreen} className="flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-950" data-testid="planning-fullscreen-btn"><Maximize className="w-4 h-4" />Plein écran</button>
            <button onClick={() => navigate('/atelier/tv')} className="flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-950" data-testid="planning-open-tv-btn"><Tv className="w-4 h-4" />Écran TV</button>
          </>
        ) : (
        <label className="flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-950 cursor-pointer" data-testid="planning-logo-upload-label">
          <ImageUp className="w-4 h-4" />Logo
          <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="planning-logo-input" />
        </label>
        )}
        <button onClick={() => window.print()} className="flex items-center gap-2 bg-[#0E1116] text-[#FACC15] font-bold text-sm rounded-full px-4 py-1.5 hover:bg-black transition-colors" data-testid="planning-print-btn"><Printer className="w-4 h-4" />Imprimer A4</button>
      </div>
      {data ? <div className="sheet-fit" style={scale ? { zoom: scale.zoom } : undefined} data-testid="planning-sheet-fit"><PlanningSheet data={data} logo={logo} atelier={atelier} height={scale?.height} /></div> : <div className="text-slate-500 py-20">Chargement…</div>}
    </div>
  );
}
